import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  AddAddressContent,
  AddressOverlayMessage,
  LoginFormValidate
} from '@com-delta-idp/mnx-login-ui';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LoginHelperUtils } from '../../../utils/login-helper.utils';
import { ERROR_CODES } from '../../constants/login-constant';
import {
  AddressLinesResponse,
  CountryListResponse
} from '../../models/add-address.interface';
import { LoginModalContent } from '../../models/aem';
import { error } from '../../models/reset-password-api.interface';
import { NextDisplayViewService } from '../../services';
import { AddAddressApiService } from '../../services/add-address/add-address-api.service';
import { AddAddressHelperService } from '../../services/add-address/add-address-helper.service';

@Injectable({
  providedIn: 'root'
})
export class AddAddressHelperFacadeService {
  aemContent!: LoginModalContent;
  unsubscribe$: Subject<void> = new Subject<void>();
  addAddressContent$ = new BehaviorSubject<AddAddressContent | null>(null);
  addAddressError$ = new BehaviorSubject<string | null>(null);
  addAddressApiService = inject(AddAddressApiService);
  helperService = inject(AddAddressHelperService);
  loginHelperUtils = inject(LoginHelperUtils);
  nextDisplayViewService = inject(NextDisplayViewService);
  router = inject(Router);

  getAddAddressContent(): AddAddressContent {
    return this.addAddressContent$.value || ({} as AddAddressContent);
  }

  addEmailAddressFormValueChange(): void {
    this.getAddAddressContent().formGroup.valueChanges.subscribe(() => {
      this.getAddAddressContent().formAttributes.errorCount =
        LoginFormValidate.validateFormFields(
          this.getAddAddressContent().formGroup
        );
      this.getAddAddressContent().formAttributes.errorAlertBanner =
        LoginFormValidate.getErrorAlert(this.getAddAddressContent().formGroup);
    });
  }

  showHideOverlayMessage(value: AddressOverlayMessage): void {
    this.getAddAddressContent().isSuccess = value.isSuccess;
    this.getAddAddressContent().hasError = value.isError;
  }

  handleError(errors: error[] = []): void {
    const errorId: string = errors[0]?.code;
    if (errorId) {
      const bffErrorMessage: string =
        this.loginHelperUtils.getErrorMessageFromAem(
          this.aemContent,
          `ERROR_MSG_${errorId}`,
          errorId
        );
      if (bffErrorMessage) {
        this.getAddAddressContent().errorMessage = bffErrorMessage;
        this.showHideOverlayMessage({ isSuccess: false, isError: true });
        return;
      }
    }
    this.handleDefaultError();
  }

  handleDefaultError(): string {
    const bffErrorMessage: string =
      this.loginHelperUtils.getErrorMessageFromAem(
        this.aemContent,
        `ERROR_MSG_${ERROR_CODES.ERROR_CODE_21}`
      );
    this.getAddAddressContent().errorMessage = bffErrorMessage;
    this.showHideOverlayMessage({ isSuccess: false, isError: true });
    return bffErrorMessage;
  }

  resetForm(): void {
    this.getAddAddressContent().formAttributes =
      this.helperService.getFormAttributes();
    this.showHideOverlayMessage({ isSuccess: false, isError: false });
    this.getAddAddressContent().errorMessage = '';
    this.getAddAddressContent().formGroup?.reset();
  }

  getAddressDetails(countryCode: string) {
    this.getAddAddressContent().formGroup.markAsUntouched();
    this.getAddAddressContent().formAttributes =
      this.helperService.getFormAttributes();
    this.addAddressApiService
      .getAddressLines(countryCode)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((response) => {
        this.getAddAddressContent().pageContent.addressFields =
          this.helperService.transformAddressLines(
            response,
            this.getAddAddressContent().formGroup
          );
        this.getAddAddressContent().formGroup.controls['state'].reset();
        this.getAddAddressContent().pageContent.stateList =
          this.helperService.stateList;
        this.getAddAddressContent().pageContent.stateLabel =
          this.helperService.stateLabel;
      });
  }

  disableActionButton(value: boolean): void {
    this.getAddAddressContent().formAttributes.disableActionButton = value;
  }

  checkApiAndAemResponse({
    ...response
  }: {
    aemResponse: LoginModalContent;
    countryCodes: CountryListResponse;
    addressLines: AddressLinesResponse;
  }): boolean {
    const { aemResponse, countryCodes, addressLines } = response;
    return (
      !aemResponse?.addAddress ||
      !countryCodes?.countryCodeList ||
      countryCodes.countryCodeList.length === 0 ||
      !addressLines?.addressFields
    );
  }
}
